/**
 * Error boundary.
 *
 * Without one, a single render error anywhere in the tree unmounts the whole console and leaves a
 * blank page — the worst kind of silent failure, and squarely against non-negotiable #6. The
 * simulation itself is unaffected: it lives in the worker, and its state is intact behind whatever
 * broke on screen. So the boundary says what happened, and offers the two things that are actually
 * useful — reload the console, or go back to the campaign.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced rather than swallowed. The worker keeps running; this is a UI fault only.
    // eslint-disable-next-line no-console
    console.error('Freeboard UI fault', error, info.componentStack);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  override render(): ReactNode {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{ height: '100%', display: 'grid', placeItems: 'center', padding: 'var(--s5)' }}
      >
        <div className="panel" style={{ maxWidth: 640, borderColor: 'var(--alarm)' }}>
          <div className="label" style={{ color: 'var(--alarm)', marginBottom: 6 }}>
            Console fault
          </div>
          <h1 className="title" style={{ fontSize: 20, margin: '0 0 8px' }}>
            The display failed, not the plant
          </h1>
          <p style={{ color: 'var(--ink-dim)', marginTop: 0 }}>
            Something in the user interface threw while rendering. The simulation runs in a separate
            worker and its state is unaffected, but this console cannot draw it. Reloading rebuilds
            the console; any unsaved progress in the current shift is lost.
          </p>
          <pre
            className="num"
            style={{
              background: 'var(--surface-0)',
              border: '1px solid var(--line)',
              borderRadius: 3,
              padding: 'var(--s2)',
              fontSize: 11,
              overflowX: 'auto',
              maxHeight: 220,
            }}
          >
            {error.message}
            {componentStack ? `\n${componentStack.split('\n').slice(0, 8).join('\n')}` : ''}
          </pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => this.setState({ error: null, componentStack: null })}>
              Try again
            </button>
            <button className="primary" onClick={() => window.location.reload()}>
              Reload console
            </button>
          </div>
        </div>
      </div>
    );
  }
}
